import {
  DocumentData,
  QueryConstraint,
  count,
  doc,
  getAggregateFromServer,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  where
} from "firebase/firestore";
import { db } from "../lib/firebase";
import { ADMIN_ANALYTICS_COLLECTION, ADMIN_ANALYTICS_DOCS, COLLECTIONS } from "./collectionNames";
import { adminCollection, dateField, logDevelopmentError, stringField } from "./firestoreHelpers";

export type OpsSignalState = "ready" | "unavailable";

export type OpsSignal = {
  id: string;
  title: string;
  value: string;
  source: string;
  note: string;
  state: OpsSignalState;
};

export type OpsEvent = {
  id: string;
  title: string;
  source: string;
  severity: string;
  detail: string;
  date: Date | null;
};

export type FounderInfrastructureMetricStatus = "healthy" | "warning" | "action_needed" | "processing" | "unavailable";

export type FounderInfrastructureMetric = {
  id: string;
  title: string;
  value: string;
  detail: string;
  status: FounderInfrastructureMetricStatus;
  source: string;
  drilldown: string;
};

export type FounderInfrastructureDriver = {
  id: string;
  title: string;
  value: string;
  detail: string;
  status: FounderInfrastructureMetricStatus;
};

export type FounderInfrastructureSummary = {
  primaryMetrics: FounderInfrastructureMetric[];
  usageMetrics: FounderInfrastructureMetric[];
  derivedMetrics: FounderInfrastructureMetric[];
  topCostDrivers: FounderInfrastructureDriver[];
  lastUpdated: Date;
};

export type SystemHealthData = {
  infrastructure: FounderInfrastructureSummary;
  signals: OpsSignal[];
  criticalEvents: OpsEvent[];
  recentPaymentFailures: OpsEvent[];
  guardianEmailFailures: OpsEvent[];
  lastUpdated: Date;
};

const RECENT_LIMIT = 30;
const FAILURE_STATUSES = ["failed", "cancelled"];
const PLATFORM_HEALTH_SOURCE = `${ADMIN_ANALYTICS_COLLECTION}/${ADMIN_ANALYTICS_DOCS.platformHealthMetrics}`;
const CRITICAL_EVENT_NAMES = new Set([
  "report_submitted",
  "safety_event_flagged",
  "guardian_access_revoked",
  "payment_failed",
  "checkout_failed",
  "upload_failed",
  "app_error",
  "firestore_permission_denied"
]);

type PlatformHealthMetrics = Record<string, unknown>;

function safeString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value : null;
}

function textFromProperties(data: DocumentData): string {
  const properties = data.properties;

  if (!properties || typeof properties !== "object" || Array.isArray(properties)) {
    return "";
  }

  return Object.entries(properties as Record<string, unknown>)
    .map(([key, value]) => `${key}: ${String(value)}`)
    .join(", ");
}

function signal(id: string, title: string, value: string, source: string, note: string, state: OpsSignalState = "ready"): OpsSignal {
  return {
    id,
    title,
    value,
    source,
    note,
    state
  };
}

function infrastructureMetric(
  id: string,
  title: string,
  value: string,
  detail: string,
  status: FounderInfrastructureMetricStatus,
  source: string,
  drilldown: string
): FounderInfrastructureMetric {
  return {
    id,
    title,
    value,
    detail,
    status,
    source,
    drilldown
  };
}

function formatCount(value: number | null): string {
  return value === null ? "Unavailable" : new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(value);
}

function formatMoneyFromCents(value: number | null): string {
  return value === null
    ? "Unavailable"
    : new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" }).format(value / 100);
}

function formatBytes(value: number | null): string {
  if (value === null) {
    return "Unavailable";
  }
  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits: value >= 1024 * 1024 * 1024 ? 1 : 0,
    style: "unit",
    unit: value >= 1024 * 1024 * 1024 ? "gigabyte" : value >= 1024 * 1024 ? "megabyte" : "kilobyte",
    unitDisplay: "short"
  }).format(value >= 1024 * 1024 * 1024 ? value / (1024 * 1024 * 1024) : value >= 1024 * 1024 ? value / (1024 * 1024) : value / 1024);
}

function divide(numerator: number | null, denominator: number | null): number | null {
  if (numerator === null || denominator === null || denominator <= 0) {
    return null;
  }
  return numerator / denominator;
}

function costStatus(value: number | null): FounderInfrastructureMetricStatus {
  if (value === null) {
    return "unavailable";
  }
  if (value <= 0) {
    return "healthy";
  }
  return "processing";
}

function volumeStatus(value: number | null): FounderInfrastructureMetricStatus {
  return value === null ? "unavailable" : "healthy";
}

async function safeCount(collectionName: string, constraints: QueryConstraint[] = []): Promise<number | null> {
  try {
    const snapshot = await getAggregateFromServer(query(adminCollection(collectionName), ...constraints), {
      total: count()
    });
    return snapshot.data().total ?? 0;
  } catch (error) {
    logDevelopmentError(error);
    return null;
  }
}

async function fetchPlatformHealthMetrics(): Promise<PlatformHealthMetrics> {
  try {
    const snapshot = await getDoc(doc(db, ADMIN_ANALYTICS_COLLECTION, ADMIN_ANALYTICS_DOCS.platformHealthMetrics));
    return snapshot.exists() ? snapshot.data() : {};
  } catch (error) {
    logDevelopmentError(error);
    return {};
  }
}

function numericPlatformMetric(metrics: PlatformHealthMetrics, keys: string[]): number | null {
  for (const key of keys) {
    const value = metrics[key];
    if (typeof value === "number" && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === "string") {
      const parsed = Number(value.replace(/[$,%\s,]/g, ""));
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }
  }
  return null;
}

function centsPlatformMetric(metrics: PlatformHealthMetrics, centsKeys: string[], dollarKeys: string[] = []): number | null {
  const cents = numericPlatformMetric(metrics, centsKeys);
  if (cents !== null) {
    return Math.round(cents);
  }
  const dollars = numericPlatformMetric(metrics, dollarKeys);
  return dollars === null ? null : Math.round(dollars * 100);
}

function topCostDriversFromPlatformMetrics(metrics: PlatformHealthMetrics): FounderInfrastructureDriver[] {
  const rawDrivers = metrics.topCostDrivers ?? metrics.infrastructureTopCostDrivers;
  if (Array.isArray(rawDrivers)) {
    return rawDrivers.slice(0, 5).map((driver, index) => {
      const record = typeof driver === "object" && driver !== null ? (driver as Record<string, unknown>) : {};
      const title = safeString(record.title) ?? safeString(record.name) ?? safeString(record.category) ?? `Driver ${index + 1}`;
      const costCents = centsPlatformMetric(record, ["costCents", "amountCents", "monthlyCostCents"], ["costUSD", "amountUSD", "monthlyCostUSD"]);
      return {
        id: safeString(record.id) ?? title.toLowerCase().replace(/\s+/g, "-"),
        title,
        value: formatMoneyFromCents(costCents),
        detail: safeString(record.detail) ?? safeString(record.reason) ?? "Cost driver from platform health metrics.",
        status: costStatus(costCents)
      };
    });
  }

  const fallbackDrivers = [
    { id: "media", title: "Media", value: centsPlatformMetric(metrics, ["mediaCostCents"], ["mediaCostUSD"]) },
    { id: "search", title: "Search", value: centsPlatformMetric(metrics, ["searchCostCents"], ["searchCostUSD"]) },
    { id: "coach-insights", title: "Coach Insights", value: centsPlatformMetric(metrics, ["coachInsightsCostCents"], ["coachInsightsCostUSD"]) },
    { id: "challenges", title: "Challenges", value: centsPlatformMetric(metrics, ["challengeCostCents"], ["challengeCostUSD"]) },
    { id: "notifications", title: "Notifications", value: centsPlatformMetric(metrics, ["notificationCostCents"], ["notificationCostUSD"]) }
  ];

  return fallbackDrivers
    .map((driver) => ({
      id: driver.id,
      title: driver.title,
      value: formatMoneyFromCents(driver.value),
      detail: driver.value === null ? "Connect cost telemetry for this driver." : "Estimated from platform health cost telemetry.",
      status: costStatus(driver.value)
    }))
    .sort((left, right) => {
      const leftValue = left.value === "Unavailable" ? -1 : Number(left.value.replace(/[$,]/g, ""));
      const rightValue = right.value === "Unavailable" ? -1 : Number(right.value.replace(/[$,]/g, ""));
      return rightValue - leftValue;
    })
    .slice(0, 5);
}

async function fetchRecentDocs(collectionName: string, dateFieldName: string, constraints: QueryConstraint[] = []): Promise<Array<{ id: string; data: DocumentData }>> {
  try {
    const snapshot = await getDocs(
      query(adminCollection(collectionName), ...constraints, orderBy(dateFieldName, "desc"), limit(RECENT_LIMIT))
    );
    return snapshot.docs.map((document) => ({ id: document.id, data: document.data() }));
  } catch (error) {
    logDevelopmentError(error);
    return [];
  }
}

async function platformHealthValue(key: string): Promise<string | null> {
  try {
    const snapshot = await getDoc(doc(db, ADMIN_ANALYTICS_COLLECTION, ADMIN_ANALYTICS_DOCS.platformHealthMetrics));
    if (!snapshot.exists()) {
      return null;
    }

    const data = snapshot.data();
    const value = data[key];
    return typeof value === "number" || typeof value === "string" ? String(value) : null;
  } catch (error) {
    logDevelopmentError(error);
    return null;
  }
}

function eventFromAnalyticsDoc(id: string, source: string, data: DocumentData, dateFieldName: string): OpsEvent {
  const eventName = stringField(data, "eventName") ?? stringField(data, "eventType") ?? "Analytics event";
  const severity = stringField(data, "severity") ?? stringField(data, "level") ?? "critical";
  const detail =
    stringField(data, "message") ??
    stringField(data, "reason") ??
    stringField(data, "screen") ??
    textFromProperties(data) ??
    "No detail available";

  return {
    id,
    title: eventName,
    source,
    severity,
    detail,
    date: dateField(data, dateFieldName) ?? dateField(data, "createdAt") ?? dateField(data, "timestamp")
  };
}

function isCriticalAnalyticsEvent(data: DocumentData): boolean {
  const eventName = (stringField(data, "eventName") ?? stringField(data, "eventType") ?? "").toLowerCase();
  const severity = (stringField(data, "severity") ?? stringField(data, "level") ?? "").toLowerCase();
  const message = (stringField(data, "message") ?? stringField(data, "reason") ?? textFromProperties(data)).toLowerCase();

  return (
    CRITICAL_EVENT_NAMES.has(eventName) ||
    severity === "critical" ||
    message.includes("critical") ||
    message.includes("permission-denied") ||
    message.includes("permission denied")
  );
}

function startOfToday(): Date {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date;
}

function startOfTomorrow(): Date {
  const date = startOfToday();
  date.setDate(date.getDate() + 1);
  return date;
}

function daysInCurrentMonth(): number {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
}

async function countToday(collectionName: string, dateFieldName = "createdAt"): Promise<number | null> {
  return safeCount(collectionName, [where(dateFieldName, ">=", startOfToday()), where(dateFieldName, "<", startOfTomorrow())]);
}

function valueWithTelemetryFallback(value: number | null, fallback: number | null): number | null {
  return value === null ? fallback : value;
}

async function fetchFounderInfrastructureSummary(platformMetrics: PlatformHealthMetrics): Promise<FounderInfrastructureSummary> {
  const todayCostCents = centsPlatformMetric(
    platformMetrics,
    ["todayCostCents", "todaysCostCents", "infrastructureTodayCostCents"],
    ["todayCostUSD", "todaysCostUSD", "infrastructureTodayCostUSD"]
  );
  const projectedMonthlyCostCents =
    centsPlatformMetric(
      platformMetrics,
      ["projectedMonthlyCostCents", "infrastructureProjectedMonthlyCostCents"],
      ["projectedMonthlyCostUSD", "infrastructureProjectedMonthlyCostUSD"]
    ) ?? (todayCostCents === null ? null : todayCostCents * daysInCurrentMonth());

  const [
    totalUsers,
    totalBookings,
    totalPostsToday,
    totalChallengesToday,
    notificationCountToday,
    stripeWebhookCountToday
  ] = await Promise.all([
    safeCount(COLLECTIONS.users),
    safeCount(COLLECTIONS.bookings),
    countToday(COLLECTIONS.posts),
    countToday(COLLECTIONS.challenges),
    countToday(COLLECTIONS.notifications),
    countToday(COLLECTIONS.stripeWebhookEvents)
  ]);

  const activeUsers =
    numericPlatformMetric(platformMetrics, ["activeUsersToday", "activeUsers", "dailyActiveUsers", "dau"]) ?? null;
  const activeSessions = numericPlatformMetric(platformMetrics, ["activeSessions", "sessionsToday", "sessionCountToday"]);
  const firestoreReads = numericPlatformMetric(platformMetrics, ["firestoreReadsToday", "firestoreReads", "firestoreReadCount"]);
  const firestoreWrites = numericPlatformMetric(platformMetrics, ["firestoreWritesToday", "firestoreWrites", "firestoreWriteCount"]);
  const cloudFunctions = numericPlatformMetric(platformMetrics, ["cloudFunctionsToday", "cloudFunctionInvocationsToday", "functionInvocationsToday"]);
  const storageBytes = numericPlatformMetric(platformMetrics, ["storageBytes", "storageBytesToday", "storageUsedBytes"]);
  const bandwidthBytes = numericPlatformMetric(platformMetrics, ["bandwidthBytesToday", "bandwidthBytes", "egressBytesToday"]);
  const notificationsSent = valueWithTelemetryFallback(
    numericPlatformMetric(platformMetrics, ["notificationsSentToday", "notificationsSent", "notificationCountToday"]),
    notificationCountToday
  );
  const stripeCalls = valueWithTelemetryFallback(
    numericPlatformMetric(platformMetrics, ["stripeCallsToday", "stripeCalls", "stripeApiCallsToday"]),
    stripeWebhookCountToday
  );
  const mediaUploaded = valueWithTelemetryFallback(
    numericPlatformMetric(platformMetrics, ["mediaUploadedToday", "mediaUploadsToday", "mediaUploaded"]),
    totalPostsToday
  );
  const searches = numericPlatformMetric(platformMetrics, ["searchesToday", "searchCountToday", "searches"]);
  const challengeUploads = valueWithTelemetryFallback(
    numericPlatformMetric(platformMetrics, ["challengeUploadsToday", "challengeUploads", "challengeUploadCountToday"]),
    totalChallengesToday
  );
  const averageFunctionRuntimeMs = numericPlatformMetric(platformMetrics, ["averageFunctionRuntimeMs", "avgFunctionRuntimeMs"]);

  const primaryMetrics = [
    infrastructureMetric(
      "today-cost",
      "Today's Cost",
      formatMoneyFromCents(todayCostCents),
      todayCostCents === null ? "Connect cost telemetry to see today's infrastructure spend." : "Estimated spend for the current day.",
      costStatus(todayCostCents),
      PLATFORM_HEALTH_SOURCE,
      "Daily infrastructure cost"
    ),
    infrastructureMetric(
      "projected-monthly-cost",
      "Projected Monthly Cost",
      formatMoneyFromCents(projectedMonthlyCostCents),
      projectedMonthlyCostCents === null
        ? "Connect monthly cost telemetry to project spend."
        : todayCostCents === null
          ? "Projected from platform health telemetry."
          : "Projected from today's cost and the current month length.",
      costStatus(projectedMonthlyCostCents),
      PLATFORM_HEALTH_SOURCE,
      "Monthly infrastructure projection"
    )
  ];

  const usageMetrics = [
    infrastructureMetric("firestore-reads", "Firestore Reads", formatCount(firestoreReads), "Read volume for Chor data surfaces.", volumeStatus(firestoreReads), PLATFORM_HEALTH_SOURCE, "Database read volume"),
    infrastructureMetric("firestore-writes", "Firestore Writes", formatCount(firestoreWrites), "Write volume from user actions and backend workflows.", volumeStatus(firestoreWrites), PLATFORM_HEALTH_SOURCE, "Database write volume"),
    infrastructureMetric("cloud-functions", "Cloud Functions", formatCount(cloudFunctions), "Callable, webhook, and scheduled backend work.", volumeStatus(cloudFunctions), PLATFORM_HEALTH_SOURCE, "Backend work volume"),
    infrastructureMetric("storage", "Storage", formatBytes(storageBytes), "Media and generated asset storage footprint.", volumeStatus(storageBytes), PLATFORM_HEALTH_SOURCE, "Storage footprint"),
    infrastructureMetric("bandwidth", "Bandwidth", formatBytes(bandwidthBytes), "Media and API egress volume.", volumeStatus(bandwidthBytes), PLATFORM_HEALTH_SOURCE, "Network egress"),
    infrastructureMetric("notifications-sent", "Notifications Sent", formatCount(notificationsSent), "Notification records created today.", volumeStatus(notificationsSent), COLLECTIONS.notifications, "Notification volume"),
    infrastructureMetric("stripe-calls", "Stripe Calls", formatCount(stripeCalls), "Stripe webhook/API activity observed today.", volumeStatus(stripeCalls), COLLECTIONS.stripeWebhookEvents, "Payment operations volume"),
    infrastructureMetric("media-uploaded", "Media Uploaded", formatCount(mediaUploaded), "Media-bearing product activity observed today.", volumeStatus(mediaUploaded), COLLECTIONS.posts, "Media upload volume"),
    infrastructureMetric("searches", "Searches", formatCount(searches), "Search demand across Chor.", volumeStatus(searches), PLATFORM_HEALTH_SOURCE, "Search demand"),
    infrastructureMetric("challenge-uploads", "Challenge Uploads", formatCount(challengeUploads), "Challenge records created today.", volumeStatus(challengeUploads), COLLECTIONS.challenges, "Challenge creation volume")
  ];

  const derivedMetrics = [
    infrastructureMetric("cost-per-user", "Cost Per User", formatMoneyFromCents(divide(todayCostCents, totalUsers)), "Today's cost divided by total users.", costStatus(divide(todayCostCents, totalUsers)), "Derived", "Cost efficiency per user"),
    infrastructureMetric("cost-per-active-user", "Cost Per Active User", formatMoneyFromCents(divide(todayCostCents, activeUsers)), "Today's cost divided by active users.", costStatus(divide(todayCostCents, activeUsers)), "Derived", "Cost efficiency per active user"),
    infrastructureMetric("cost-per-booking", "Cost Per Booking", formatMoneyFromCents(divide(todayCostCents, totalBookings)), "Today's cost divided by canonical bookings.", costStatus(divide(todayCostCents, totalBookings)), "Derived", "Booking cost efficiency"),
    infrastructureMetric("cost-per-upload", "Cost Per Upload", formatMoneyFromCents(divide(todayCostCents, mediaUploaded)), "Today's cost divided by media uploads.", costStatus(divide(todayCostCents, mediaUploaded)), "Derived", "Upload cost efficiency"),
    infrastructureMetric("cost-per-challenge", "Cost Per Challenge", formatMoneyFromCents(divide(todayCostCents, challengeUploads)), "Today's cost divided by challenge uploads.", costStatus(divide(todayCostCents, challengeUploads)), "Derived", "Challenge cost efficiency"),
    infrastructureMetric("average-function-runtime", "Average Function Runtime", averageFunctionRuntimeMs === null ? "Unavailable" : `${Math.round(averageFunctionRuntimeMs)} ms`, "Average backend function runtime.", volumeStatus(averageFunctionRuntimeMs), PLATFORM_HEALTH_SOURCE, "Backend runtime"),
    infrastructureMetric("average-firestore-reads-per-session", "Average Firestore Reads Per Session", divide(firestoreReads, activeSessions) === null ? "Unavailable" : formatCount(Math.round(divide(firestoreReads, activeSessions) ?? 0)), "Firestore reads divided by active sessions.", volumeStatus(divide(firestoreReads, activeSessions)), "Derived", "Session read efficiency")
  ];

  return {
    primaryMetrics,
    usageMetrics,
    derivedMetrics,
    topCostDrivers: topCostDriversFromPlatformMetrics(platformMetrics),
    lastUpdated: new Date()
  };
}

async function fetchCriticalEvents(): Promise<OpsEvent[]> {
  const [nextParticipationDocs, analyticsDocs] = await Promise.all([
    fetchRecentDocs(COLLECTIONS.nextParticipationAnalyticsEvents, "createdAt"),
    fetchRecentDocs(COLLECTIONS.analyticsEvents, "timestamp")
  ]);

  return [
    ...nextParticipationDocs.map((document) =>
      eventFromAnalyticsDoc(document.id, COLLECTIONS.nextParticipationAnalyticsEvents, document.data, "createdAt")
    ),
    ...analyticsDocs.map((document) => eventFromAnalyticsDoc(document.id, COLLECTIONS.analyticsEvents, document.data, "timestamp"))
  ]
    .filter((event) => {
      const normalized = `${event.title} ${event.severity} ${event.detail}`.toLowerCase();
      return (
        CRITICAL_EVENT_NAMES.has(event.title.toLowerCase()) ||
        normalized.includes("critical") ||
        normalized.includes("permission-denied") ||
        normalized.includes("permission denied")
      );
    })
    .sort((left, right) => (right.date?.getTime() ?? 0) - (left.date?.getTime() ?? 0))
    .slice(0, 12);
}

function guardianEmailEvent(id: string, data: DocumentData): OpsEvent {
  return {
    id,
    title: "Guardian email failure",
    source: COLLECTIONS.guardianApprovals,
    severity: "warning",
    detail: stringField(data, "lastEmailError") ?? "Invite status failed.",
    date: dateField(data, "updatedAt") ?? dateField(data, "lastSentAt") ?? dateField(data, "requestedAt")
  };
}

async function fetchGuardianEmailFailures(): Promise<OpsEvent[]> {
  const docs = await fetchRecentDocs(COLLECTIONS.guardianApprovals, "updatedAt", [where("inviteStatus", "==", "failed")]);
  return docs.map((document) => guardianEmailEvent(document.id, document.data));
}

function paymentFailureEvent(id: string, data: DocumentData): OpsEvent {
  return {
    id,
    title: stringField(data, "sourceType") ?? stringField(data, "type") ?? "Payment failure",
    source: COLLECTIONS.marketplaceTransactions,
    severity: "warning",
    detail: stringField(data, "status") ?? "Payment status unavailable.",
    date: dateField(data, "updatedAt") ?? dateField(data, "createdAt")
  };
}

async function fetchPaymentFailures(): Promise<OpsEvent[]> {
  const docs = await fetchRecentDocs(COLLECTIONS.marketplaceTransactions, "updatedAt", [where("status", "in", FAILURE_STATUSES)]);
  return docs.map((document) => paymentFailureEvent(document.id, document.data));
}

async function countCriticalEvents(events: OpsEvent[]): Promise<number | null> {
  if (events.length > 0) {
    return events.length;
  }

  const platformValue = await platformHealthValue("criticalAnalyticsEvents");
  return platformValue === null ? null : Number(platformValue);
}

async function appErrorSignal(): Promise<OpsSignal> {
  const appErrorMetric = await platformHealthValue("recentAppErrors");
  if (appErrorMetric !== null) {
    return signal("appErrors", "Recent app errors", appErrorMetric, `${ADMIN_ANALYTICS_COLLECTION}/${ADMIN_ANALYTICS_DOCS.platformHealthMetrics}`, "Read from aggregated platform health metrics.");
  }

  // TODO: Add a server/client-owned app error collection or adminAnalytics.recentAppErrors aggregate.
  return signal("appErrors", "Recent app errors", "Unavailable", "TODO", "No persisted app error log collection was found.", "unavailable");
}

export async function fetchSystemHealthData(): Promise<SystemHealthData> {
  const platformMetrics = await fetchPlatformHealthMetrics();
  const [infrastructure, guardianEmailFailures, paymentFailures, criticalEvents, failedSignupsMetric, appErrors] = await Promise.all([
    fetchFounderInfrastructureSummary(platformMetrics),
    fetchGuardianEmailFailures(),
    fetchPaymentFailures(),
    fetchCriticalEvents(),
    platformHealthValue("failedSignups"),
    appErrorSignal()
  ]);
  const criticalEventCount = await countCriticalEvents(criticalEvents);

  const signals: OpsSignal[] = [
    failedSignupsMetric !== null
      ? signal("failedSignups", "Failed signups", failedSignupsMetric, `${ADMIN_ANALYTICS_COLLECTION}/${ADMIN_ANALYTICS_DOCS.platformHealthMetrics}`, "Read from aggregated platform health metrics.")
      : // TODO: Persist signup/auth failure events or expose a server-owned failedSignups aggregate.
        signal("failedSignups", "Failed signups", "Unavailable", "TODO", "No failed signup log or aggregate was found.", "unavailable"),
    signal("guardianEmailFailures", "Guardian email failures", String(guardianEmailFailures.length), COLLECTIONS.guardianApprovals, "guardianApprovals with inviteStatus failed."),
    // TODO: Persist email verification send/bounce/failure events or expose adminAnalytics.emailVerificationIssues.
    signal("emailVerificationIssues", "Email verification issues", "Unavailable", "TODO", "No email verification issue log or aggregate was found.", "unavailable"),
    // TODO: Persist upload failures from Firebase Storage/profile/media flows into a dedicated ops collection or aggregate.
    signal("uploadFailures", "Upload failures", "Unavailable", "TODO", "No upload failure log or aggregate was found.", "unavailable"),
    signal("paymentFailures", "Payment failures", String(paymentFailures.length), COLLECTIONS.marketplaceTransactions, "marketplace_transactions with failed/cancelled status."),
    // TODO: Persist client Firestore permission-denied errors into a dedicated app error collection or platform health aggregate.
    signal("firestorePermissionErrors", "Firestore permission errors", "Unavailable", "TODO", "No Firestore permission error log or aggregate was found.", "unavailable"),
    criticalEventCount !== null
      ? signal("criticalAnalyticsEvents", "Recent critical analytics events", String(criticalEventCount), `${COLLECTIONS.nextParticipationAnalyticsEvents}, ${COLLECTIONS.analyticsEvents}`, "Recent bounded analytics reads filtered for critical/safety/permission events.")
      : signal("criticalAnalyticsEvents", "Recent critical analytics events", "Unavailable", "TODO", "No critical analytics events or aggregate were found.", "unavailable"),
    appErrors
  ];

  return {
    infrastructure,
    signals,
    criticalEvents,
    recentPaymentFailures: paymentFailures,
    guardianEmailFailures,
    lastUpdated: new Date()
  };
}
